import {
  LOCALE_LABELS,
  LOCALE_NATIVE_LABELS,
  SUPPORTED_LOCALES,
  normalizeLocale,
  type SupportedLocale,
} from '@/i18n/config';
import { LANGUAGE_OPTION_COPY } from '@/i18n/language-option-copy';
import {
  clearPendingSignupLocale,
  setPendingSignupLocale,
} from '@/lib/auth-locale-preference';
import { resolveInitialTimezone } from '@/lib/regional-timezone';

export function compareNativeLocaleLabels(
  left: SupportedLocale,
  right: SupportedLocale
): number {
  return LOCALE_NATIVE_LABELS[left].localeCompare(
    LOCALE_NATIVE_LABELS[right],
    'en',
    { sensitivity: 'base' }
  );
}

export type AppLanguageOption = {
  locale: SupportedLocale;
  /** The language's own name, e.g. "Español". */
  nativeLabel: string;
  /** Name in the active UI language, omitted when it matches the native name. */
  localizedLabel: string | null;
  /** English name, e.g. "Spanish" — secondary hint and search keyword. */
  englishLabel: string;
  isCurrent: boolean;
};

/** Every supported app language, sorted by its native name. */
export function getAppLanguageOptions(
  currentLocale: string | null | undefined
): AppLanguageOption[] {
  const current = normalizeLocale(currentLocale);
  return [...SUPPORTED_LOCALES].sort(compareNativeLocaleLabels).map((locale) => {
    const nativeLabel = LOCALE_NATIVE_LABELS[locale];
    const localizedLabel = LANGUAGE_OPTION_COPY[current][locale];
    return {
      locale,
      nativeLabel,
      localizedLabel: localizedLabel === nativeLabel ? null : localizedLabel,
      englishLabel: LOCALE_LABELS[locale],
      isCurrent: locale === current,
    };
  });
}

type ProfileRegionalSettings = {
  settings: {
    timezone?: string;
    timezoneSource?: 'system' | 'user';
  };
};

export type PersistAppLocaleChangeArgs = {
  locale: string;
  isAuthenticated: boolean;
  browserTimezone: string | null;
  fetchProfile: () => Promise<ProfileRegionalSettings | null>;
  updateRegionalSettings: (args: {
    language: string;
    timezone: string;
  }) => Promise<unknown>;
};

/**
 * Persists an in-app language choice the same way the Profile settings
 * "Language" control does: the profile's `settings.language` (the source the
 * server reads in `src/i18n/request.ts`) plus the `imai-locale` cookie.
 *
 * The timezone sent alongside is the one the settings page would show, so a
 * language change never moves the user's timezone.
 */
export async function persistAppLocaleChange({
  locale,
  isAuthenticated,
  browserTimezone,
  fetchProfile,
  updateRegionalSettings,
}: PersistAppLocaleChangeArgs): Promise<SupportedLocale> {
  const nextLocale = normalizeLocale(locale);

  if (!isAuthenticated) {
    return setPendingSignupLocale(nextLocale);
  }

  const profile = await fetchProfile();
  await updateRegionalSettings({
    language: nextLocale,
    timezone: resolveInitialTimezone(
      profile?.settings.timezone,
      browserTimezone,
      profile?.settings.timezoneSource
    ),
  });
  clearPendingSignupLocale(nextLocale);
  return nextLocale;
}
